import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  formatManches,
  formatSetScore,
  isWinner,
  statusChipColor,
  statusLabel,
} from "./displayHelpers";
import PlayerIdentityLabel from "./PlayerIdentityLabel";
import { statusChipSx } from "./resultatsTheme";
import type { Game, PlayerDirectory } from "./types";
import styles from "./Resultats.module.css";

interface BracketMatchCardProps {
  game: Game;
  players: PlayerDirectory;
  highlightedLicence?: string | null;
  onSelectLicence?: (licence: string) => void;
  pathHighlighted?: boolean;
}

export default function BracketMatchCard({
  game,
  players,
  highlightedLicence = null,
  onSelectLicence,
  pathHighlighted = false,
}: BracketMatchCardProps): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const manches = formatManches(game);
  const isExempt = game.status === "EXEMPT";
  const isForfeit = game.status === "FORFAIT";
  const isSpecial = isExempt || isForfeit || game.status === "ABANDON";
  const slots =
    game.players.length >= 2 ? game.players : [game.players[0] ?? null, null];
  const showSetScores =
    game.sets.length > 0 &&
    (game.status === "TERMINE" ||
      game.status === "EN_COURS" ||
      game.status === "ABANDON");
  const involvesHighlight =
    Boolean(highlightedLicence) &&
    game.players.includes(highlightedLicence);

  return (
    <Box
      className={`${styles.bracketMatchCard}${
        pathHighlighted || involvesHighlight
          ? ` ${styles.bracketMatchCardPath}`
          : ""
      }`}
      component="article"
      data-match-id={game.id}
      sx={{
        border: `1px solid ${
          pathHighlighted || involvesHighlight
            ? alpha(theme.palette.secondary.main, isDark ? 0.65 : 0.75)
            : theme.palette.divider
        }`,
        bgcolor: theme.palette.background.paper,
        backgroundImage: isDark
          ? `linear-gradient(165deg, ${alpha(
              theme.palette.secondary.main,
              0.06
            )} 0%, transparent 50%)`
          : `linear-gradient(165deg, ${alpha(
              theme.palette.primary.main,
              0.04
            )} 0%, transparent 50%)`,
        boxShadow: isDark
          ? `0 6px 18px ${alpha(theme.palette.common.black, 0.28)}`
          : `0 4px 14px ${alpha(theme.palette.primary.main, 0.07)}`,
        transition:
          "border-color 160ms ease, box-shadow 160ms ease, outline-color 160ms ease",
        ...(pathHighlighted || involvesHighlight
          ? {
              outline: `2px solid ${alpha(
                theme.palette.secondary.main,
                0.55
              )}`,
              outlineOffset: 1,
            }
          : {}),
      }}
    >
      <Box className={styles.bracketMatchHeader}>
        <Typography className={styles.bracketMatchLabel} color="text.secondary">
          {game.label || `Match ${game.order ?? game.id}`}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={statusChipColor(game.status)}
          label={statusLabel(game.status)}
          sx={statusChipSx(theme)}
        />
      </Box>

      {slots.map((licence, index) => {
        const winner = isWinner(licence, game);
        const loser =
          Boolean(game.winner) &&
          !winner &&
          Boolean(licence) &&
          game.status !== "EXEMPT";
        const forfeited =
          isForfeit &&
          game.forfeitSlot != null &&
          game.forfeitSlot === index + 1;
        const sideScore = showSetScores ? game.setWins[index] ?? 0 : null;
        const emptyExemptSlot = isExempt && !licence;
        const isSelected =
          Boolean(licence) && licence === highlightedLicence;

        return (
          <Box
            key={`${game.id}-p${index}`}
            className={`${styles.bracketPlayerRow}${
              winner ? ` ${styles.bracketPlayerRowWinner}` : ""
            }${isSelected ? ` ${styles.bracketPlayerRowSelected}` : ""}`}
            role={licence && onSelectLicence ? "button" : undefined}
            tabIndex={licence && onSelectLicence ? 0 : undefined}
            onClick={() => {
              if (licence && onSelectLicence) onSelectLicence(licence);
            }}
            onKeyDown={(event) => {
              if (!licence || !onSelectLicence) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelectLicence(licence);
              }
            }}
            sx={{
              cursor: licence && onSelectLicence ? "pointer" : undefined,
              color: loser ? "text.secondary" : "text.primary",
              bgcolor: isSelected
                ? alpha(theme.palette.secondary.main, isDark ? 0.28 : 0.18)
                : winner
                  ? alpha(
                      theme.palette.mode === "dark"
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                      isDark ? 0.22 : 0.1
                    )
                  : alpha(
                      isDark
                        ? theme.palette.common.white
                        : theme.palette.grey[900],
                      isDark ? 0.06 : 0.03
                    ),
            }}
          >
            {emptyExemptSlot ? (
              <Typography
                component="span"
                sx={{ fontStyle: "italic", fontWeight: 500 }}
              >
                Exempt
              </Typography>
            ) : (
              <PlayerIdentityLabel
                licence={licence}
                directory={players}
                emptyLabel={isExempt ? "Exempt" : "À déterminer"}
                winner={winner}
                suffix={forfeited ? " · F" : ""}
                dense
              />
            )}
            {sideScore != null && (
              <Typography
                component="span"
                className={styles.bracketSideScore}
                color={winner ? "text.primary" : "text.secondary"}
              >
                {sideScore}
              </Typography>
            )}
          </Box>
        );
      })}

      {isSpecial && !showSetScores && (
        <Typography
          className={styles.specialNote}
          color="text.secondary"
          component="p"
          sx={{ m: 0 }}
        >
          {formatSetScore(game)}
        </Typography>
      )}

      {manches && (
        <Typography
          className={styles.bracketManches}
          color="text.secondary"
          component="p"
          sx={{ m: 0 }}
        >
          {manches}
        </Typography>
      )}
    </Box>
  );
}
